import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  UserGroupIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  StarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import RatingModal from '../components/RatingModal';

const MyContractor = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [ratingModal, setRatingModal] = useState({ isOpen: false, contractorId: null, contractorName: '' });

  // 내 프로젝트에서 담당 소장 정보 조회
  const { data: myContractors, isLoading, refetch } = useQuery(
    ['my-contractors'],
    () => api.get('/projects/my-contractors').then(res => res.data),
    {
      enabled: user?.user_type === 'customer' || user?.user_type === 'vip_customer'
    }
  );

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-4 w-4 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-4 w-4 text-gray-300" />;
      }
    });
  };

  if (user?.user_type !== 'customer' && user?.user_type !== 'vip_customer') {
    navigate('/projects');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="animate-pulse">
          <div className="bg-white px-4 py-6">
            <div className="h-8 bg-gray-200 rounded-lg w-32 mb-2"></div>
          </div>
          <div className="p-4 space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
                <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded-lg w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const contractors = myContractors || [];
  const uniqueContractors = contractors.filter((contractor, index, self) =>
    index === self.findIndex((c) => c.id === contractor.id)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white sticky top-0 z-20 shadow-sm">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <UserGroupIcon className="h-7 w-7 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">내 담당 소장님</h1>
          </div>
        </div>
      </div>

      {/* 소장 목록 */}
      <div className="p-4">
        {uniqueContractors.length > 0 ? (
          <div className="space-y-3">
            {uniqueContractors.map((contractor) => (
              <div key={contractor.id}>
                <Link
                  to={`/contractors/${contractor.id}`}
                  className="block"
                >
                  <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all p-4 border border-gray-200 hover:border-gray-300">
                    <div className="flex items-start gap-4">
                      {/* 프로필 이미지 */}
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">
                          {contractor.name.charAt(0)}
                        </span>
                      </div>

                      {/* 정보 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-base font-bold text-gray-900">
                            {contractor.name} 소장님
                          </h3>
                          <div className="flex items-center gap-2">
                            {contractor.projectCount > 5 && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                                <CheckCircleIcon className="h-3 w-3" />
                                담당
                              </span>
                            )}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setRatingModal({
                                  isOpen: true,
                                  contractorId: contractor.id,
                                  contractorName: contractor.name
                                });
                              }}
                              className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                            >
                              <PencilSquareIcon className="h-3.5 w-3.5" />
                              평가하기
                            </button>
                          </div>
                        </div>
                        
                        {/* 회사명 숨김 - 고객/VIP는 볼 수 없음 */}
                        {/* <p className="text-sm text-gray-600 mb-2">{contractor.company_name}</p> */}

                        {/* 평점 */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-0.5">
                            {renderStars(contractor.rating || 0)}
                          </div>
                          <span className="text-sm text-gray-600">
                            {contractor.rating?.toFixed(1) || '0.0'}
                          </span>
                        </div>

                        {/* 전문 분야 */}
                        {contractor.specialties && (
                          <div className="flex items-center gap-1.5 mb-2">
                            <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <div className="flex flex-wrap gap-1">
                              {contractor.specialties.split(',').slice(0, 3).map((specialty, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                                >
                                  {specialty.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 담당 프로젝트 */}
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span className="font-medium">담당 프로젝트 {contractor.myProjectCount || 0}건</span>
                          {contractor.currentProject && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600">{contractor.currentProject}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* 화살표 */}
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </div>
                </Link>

                {/* 연락처 정보 숨김 - 고객/VIP는 볼 수 없음 */}
                {/* {contractor.phone && (
                  <div className="mt-2 bg-blue-50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">연락처</span>
                    </div>
                    <a 
                      href={`tel:${contractor.phone}`}
                      className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                    >
                      {contractor.phone}
                    </a>
                  </div>
                )} */}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <ExclamationCircleIcon className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium mb-2">담당 소장님이 없습니다</p>
            <p className="text-sm text-gray-400 text-center">
              프로젝트가 진행되면 담당 소장님이 배정됩니다
            </p>
          </div>
        )}
      </div>

      {/* 평가 모달 */}
      <RatingModal
        isOpen={ratingModal.isOpen}
        onClose={() => setRatingModal({ isOpen: false, contractorId: null, contractorName: '' })}
        onReviewComplete={() => {
          // 평가 완료 후 목록 새로고침
          refetch();
        }}
        contractorId={ratingModal.contractorId}
        contractorName={ratingModal.contractorName}
      />
    </div>
  );
};

export default MyContractor;